/* eslint-disable @typescript-eslint/ban-types */

import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import ModalSubsanacionDocumentos from '@/components/ModalSubsanacionDocumentos';
import SolicitudesDocumentacionFilter, {
	type SolicitudesDocumentacionFilterRef,
} from '@/components/SolicitudesDocumentacionFilter';
import PageTabs from '@/containers/PageTabs';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useRouteTramite from '@/hooks/useRouteTramite';

import { ROUTES_LABEL } from '@/constants/routes.constants';
import { ESTADO_SUBSANACION, TIPO_DOCUMENTO } from '@/constants/tramite.constants';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import type { ISolicitudDocumentacion } from '@/interfaces/model/don.interface';

const TAB_ID = {
	pendientes: 'pendientes',
	atendidas: 'atendidas',
};

export const DocumentacionPendientes = ({
	onSendDocumentacion,
	tableRef,
}: {
	readonly tableRef: RefObject<SolicitudesDocumentacionFilterRef | null>;
	readonly onSendDocumentacion: (rowData: ISolicitudDocumentacion) => void;
}) => (
	<SolicitudesDocumentacionFilter
		ref={tableRef}
		idEstado={ESTADO_SUBSANACION.PENDIENTE}
		onSendDocumentacion={onSendDocumentacion}
	/>
);

export const DocumentacionAtendidas = ({ onViewDetail }: { readonly onViewDetail: (rowData: ISolicitudDocumentacion) => void }) => (
	<SolicitudesDocumentacionFilter idEstado={ESTADO_SUBSANACION.SUBSANADA} onViewDetail={onViewDetail} />
);

const TramiteSolicitudDocumentacion = (): ReactElement => {
	const navigate = useNavigate();
	const ROUTES_TRAMITE = useRouteTramite();
	const tableRef = useRef<SolicitudesDocumentacionFilterRef>(null);
	const { isShowing, openModal, closeModal } = useModal({});
	const [dataSubsanacion, setDataSubsanacion] = useState<ISolicitudDocumentacion>();
	const { appendBreadcrumb, replaceLastBreadcrumb } = usePageContext();
	const { getTramiteData } = useGlobalContext();

	const onViewDetail = (rowData: ISolicitudDocumentacion) => {
		const { tramite, tipo, id } = rowData;
		const path = generatePath(`${ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_DETALLE}/${tramite.id}/${tipo.id}/${id}`, {
			tab: location.pathname.includes('atendidas') ? 'atendidas' : 'pendientes',
		});
		return navigate(path);
	};

	const defaultActiveTab = ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_PENDIENTES
		? TAB_ID.pendientes
		: TAB_ID.atendidas;

	const onSendDocumentacion = (rowData: ISolicitudDocumentacion) => {
		setDataSubsanacion(rowData);
		openModal();
	};

	const onSubmitSubsanacion = () => {
		closeModal();
		tableRef.current?.reload();
	};

	useEffect(() => {
		const breadcrumbTramite = getBreadcrumbTramite(getTramiteData());
		appendBreadcrumb(ROUTES_LABEL.SOLICITUDES_DOCUMENTACION, [breadcrumbTramite, { label: ROUTES_LABEL.SOLICITUDES_DOCUMENTACION, url: location.pathname }]);
	}, []);
	return (
		<>
			<PageTabs
				hasHelp
				title="Solicitudes de documentación"
				defaultActive={defaultActiveTab}
				tabs={[
					{
						key: TAB_ID.pendientes,
						header: 'Pendientes',
						content: <DocumentacionPendientes tableRef={tableRef} onSendDocumentacion={onSendDocumentacion} />,
						assignUrl: ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_PENDIENTES
					},
					{
						key: TAB_ID.atendidas,
						header: 'Atendidas',
						content: <DocumentacionAtendidas onViewDetail={onViewDetail} />,
						assignUrl: ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_ATENDIDAS
					},
				]}
				onTabChange={(index) => replaceLastBreadcrumb('Solicitudes de documentación', index === 'pendientes' ? ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_PENDIENTES : ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_ATENDIDAS )}
			/>
			{dataSubsanacion && (
				<ModalSubsanacionDocumentos
					data={{
						idTramite: dataSubsanacion.tramite.id,
						id: dataSubsanacion.id,
						idTipo: dataSubsanacion.tipo.id,
					}}
					idTipoDocumento={TIPO_DOCUMENTO.OTROS}
					isOpen={isShowing}
					onClose={closeModal}
					onSubmit={onSubmitSubsanacion}
				/>
			)}
		</>
	);
};

export default TramiteSolicitudDocumentacion;
